import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { CircularProgress } from '@material-ui/core';
import sessionsService from '../../services/sessions';
import sessionsPrepareService, { Pitch } from '../../services/sessionsPrepare';
import { Video, timeSpanToSeconds } from '../../services/sessionModels';
import { refreshSession } from '../Util/useSession';
import { models, Calibration } from '@balljames/calibration';
import { useSession, useFrame } from '@balljames/videosync';
import { validCalibration } from './SessionCalibration';
import { fetchPost } from '../Util/fetchAPI';

interface Props {
  videoid: string;
  pitchid: string;
  show: boolean;
  isValid: (url: string, isvalid: boolean) => void;
  useInitKeypoints: boolean;
}

export const apikey =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJiYWxsamFtZXMiLCJhY2NvdW50IjoiYmFsbGphbWVzLWVuZ2luZSJ9.s8V54DAY5vBPEfsQBTxpPepKtkbkVrwZP6CL5eWR1Kg';
export const baseUrl = process.env.REACT_APP_PORTAL_URL + '/gateway/videosync';

const SessionCalibrationView: React.FunctionComponent<Props> = ({ videoid, pitchid, isValid, show, useInitKeypoints }) => {
  const [pitch, setPitch] = useState<Pitch | null>(null);
  const [video, setVideo] = useState<Video | null>(null);
  const [matchingVideo, setMatchingVideo] = useState<Video | null>(null);
  const [image, setImage] = useState<HTMLImageElement>();
  const [imageUrl, setImageUrl] = useState<string>();

  useEffect(() => {
    if (!pitchid) return;
    sessionsPrepareService.getPitch(pitchid).then(setPitch);
  }, [pitchid]);

  const loadVideo = useCallback(() => sessionsService.getVideo(videoid).then(setVideo), [videoid]);

  useEffect(() => {
    loadVideo();
    sessionsPrepareService
      .getMatchingVideo(videoid)
      .then((matching) => setMatchingVideo(matching || null))
      .catch(() => setMatchingVideo(null));
  }, [videoid, loadVideo]);

  const session = useSession(apikey, video?.url || undefined, baseUrl);
  const img: ImageBitmap | undefined = useFrame(
    session,
    1080,
    apikey,
    Math.floor(((video ? timeSpanToSeconds(video.length) : 0) || 1000) * (video?.fps || 25) / 2),
    1,
    baseUrl,
    video?.fps || undefined
  );

  const [initKeypoints, setInitKeypoints] = useState<number[][] | undefined>(undefined);

  useEffect(() => {
    if (show && img && img.height > 0 && img.width > 0) {
      // create a canvas
      const canvas = document.createElement('canvas');
      // resize it to the size of our ImageBitmap
      canvas.width = img.width;
      canvas.height = img.height;
      // try to get a bitmaprenderer context
      // let ctx = canvas.getContext('bitmaprenderer');
      // if (ctx) {
      //   // transfer the ImageBitmap to it
      //   ctx.transferFromImageBitmap(img);
      // } else {
      // in case someone supports createImageBitmap only
      // twice in memory...
      canvas.getContext('2d')?.drawImage(img, 0, 0);
      // }
      const canvas2 = document.createElement('canvas');
      canvas2.width = video?.width || 0;
      canvas2.height = video?.height || 0;
      const ctx2 = canvas2.getContext('2d');
      ctx2?.drawImage(canvas, 0, 0, canvas2.width, canvas2.height);
      // get it back as a Blob
      const dataUrl = canvas2.toDataURL();
      var newimg = document.body.appendChild(new Image());
      setImageUrl(dataUrl);
      newimg.src = dataUrl;
      newimg.onload = () => {
        setImage(newimg);
      };
    }
  }, [img, video, show]);

  useEffect(() => {
    if (video) {
      isValid(video.url || '', validCalibration(video.calibration));
    }
  }, [isValid, video]);

  const updateCalibration = useCallback(
    async (calibration: string, clickPoints: string, calibrationImage: string) => {
      if (!video) return;
      await sessionsService.updateVideo(video.id, {
        url: video.url,
        sortOrder: video.sortOrder,
        syncOffset: video.syncOffset,
        calibration,
        clickPoints,
      });
      await loadVideo();
      refreshSession(video.sessionId);
      await sessionsPrepareService.updateCalibrationImage(video.id, calibrationImage);
    },
    [video, loadVideo]
  );

  const model = useMemo(() => {
    if (pitch?.type)
      return (models as { [key: string]: any })[pitch.type](
        (pitch.fieldWidth || 68000) / 1000,
        (pitch.fieldLength || 105000) / 1000
      );
    return models.FullPitchExtendedModel((pitch?.fieldWidth || 68000) / 1000, (pitch?.fieldLength || 105000) / 1000);
  }, [pitch]);

  const onSave = useCallback(
    (calib, clickpoints) => {
      if (imageUrl) {
        updateCalibration(JSON.stringify(calib), JSON.stringify(clickpoints), imageUrl);
      }
    },
    [imageUrl, updateCalibration]
  );

  useEffect(() => {
    if (
      imageUrl &&
      ((video && !video.clickPoints && matchingVideo?.clickPoints) ||
        (video && !video.calibration && matchingVideo?.calibration))
    ) {
      if(useInitKeypoints) return;
      updateCalibration(matchingVideo?.calibration || '', matchingVideo?.clickPoints || '', imageUrl);
    }
  }, [video, matchingVideo, useInitKeypoints, imageUrl, updateCalibration]);

  useEffect(() => {
    if(!image) return;

    let i = 0;
    const wc = model.worldCoords.map((t: number[]) => [i++, t[1], t[0], 0])
    const imgSrc: string = image.src.replace("data:image/png;base64,", "")
    
    const imgData = {
      world_points: wc,
      image: imgSrc,
    };

    fetchPost('/gateway/calibrations/keypoints', imgData).then((data: number[][]) => {
      if(data) {
        setInitKeypoints(data);
      }
    });
  }, [image, model]);

  const clickPoints = useMemo(() => {
    //end test
    if (!video?.clickPoints) {
      // if we want to use our init keypoints, use that
      if(useInitKeypoints) {
        if(!initKeypoints) return undefined;

        const initKps: number[][] = [];
        let prevWc: number[] = [0, 0];
        let prevKp: number[] = [0, 0];
        for(let i: number = 0; i < model.worldCoords.length; ++i) {
          const foundIkp: number[] | undefined = initKeypoints.find((ikp: number[]) => ikp[0] === i);

          if(foundIkp) {
            prevWc = model.worldCoords[i];
            prevKp = foundIkp;
            initKps.push([foundIkp[1], foundIkp[2]]);
          } else {
            const offset: number[] = [prevKp[1] + prevWc[0]-model.worldCoords[i][0], prevKp[2] + prevWc[1] -model.worldCoords[i][1]]; 
            initKps.push(offset);
          }
        }
        return JSON.stringify(initKps);
      } else {
        // else if we have previous data, use that.
        if(matchingVideo?.clickPoints) {
          return matchingVideo.clickPoints;
        } else {
          return "[]";
        }
      }
    }
    return video.clickPoints;
  }, [useInitKeypoints, video, matchingVideo, initKeypoints, model]);
  
  const calibration = useMemo(() => {
    if (!video?.calibration) {
      return matchingVideo?.calibration;
    }
    return video.calibration;
  }, [video, matchingVideo]);

  console.log('ddd', process.env.REACT_APP_CALIBRATION_URL)

  return image && ((!useInitKeypoints && clickPoints) || (useInitKeypoints && clickPoints)) ? (
    <>
      <Calibration
        defaultProjection
        model={model}
        image={image}
        baseUrl={process.env.REACT_APP_CALIBRATION_URL}
        cameraType="Universal"
        saveCalibration={onSave}
        setDirty={() => {}}
        previousPoints={clickPoints && clickPoints !== "[]" ? JSON.parse(clickPoints) : undefined}
        previousCalibration={calibration ? JSON.parse(calibration) : undefined}
      />
    </>
  ) : (
    <CircularProgress />
  );
};

export default SessionCalibrationView;
